/**
 * Hooks API pour la gestion des abonnements
 *
 * Fournit une interface React pour interagir avec l'API des abonnements
 * en utilisant SWR pour la gestion du cache et de la synchronisation.
 * Intègre la synchronisation multi-onglets et la détection de visibilité PWA.
 *
 * @module SubscriptionHooks
 * @author Équipe Brydge
 * @since 2.0.0
 */

import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import {useMemo, useState} from 'react';
import {useSWRWithSync} from 'packages/common/hooks/useSWRWithSync';
import {
  APP_EVENTS,
  notifySubscriptionsUpdated,
} from 'packages/common/utils/appEventSync';

const fetcher = (url) => axiosInstance.get(url).then((res) => res.data);

/**
 * Hook pour récupérer un plan spécifique par module et tier
 * @param {string} companyId - ID de l'entreprise
 * @param {string} module - Module (restaurant/wellness)
 * @param {string} tier - Tier (standard/premium)
 * @return {Object} Plan spécifique avec état de chargement
 */
export const useSubscriptionPlan = (companyId, module, tier) => {
  const endpointUrl = companyId && module && tier ?
    endpoints.app.subscription.planByModuleAndTier(companyId, module, tier) :
    null;

  const {data, error, mutate, isValidating} = useSWRWithSync(
      endpointUrl,
      fetcher,
      {
        eventTypes: APP_EVENTS.SUBSCRIPTIONS.LIST_UPDATED,
        companyId,
      },
  );

  return {
    plan: data?.success ? data.data : null,
    isLoading: !error && !data && !!(companyId && module && tier),
    isValidating,
    isError: !!error,
    refresh: mutate,
  };
};

/**
 * Hook pour récupérer l'abonnement actuel d'une entreprise pour un module spécifique
 * @param {string} companyId - ID de l'entreprise
 * @param {string} module - Module optionnel (restaurant/wellness)
 * @return {Object} Abonnement actuel avec informations du plan
 */
export const useCurrentSubscription = (companyId, module = null) => {
  const endpointUrl = companyId ? endpoints.app.subscription.current(companyId, module) : null;

  const {data, error, mutate, isValidating} = useSWRWithSync(
      endpointUrl,
      fetcher,
      {
        eventTypes: APP_EVENTS.SUBSCRIPTIONS.LIST_UPDATED,
        companyId,
        swrOptions: {
          onError: () => {},
        },
      },
  );

  const subscription = useMemo(() => {
    if (!data?.success || !data?.data) return null;

    const sub = data.data;

    // Utilise isActive du backend, ajoute uniquement les champs dérivés manquants
    return {
      ...sub,
      isCanceling: sub.cancelAtPeriodEnd && sub.isActive,
      hasScheduledDowngrade: !!(sub.scheduledDowngrade && sub.scheduledDowngrade.newPlanId && sub.scheduledDowngrade.effectiveDate),
      scheduledDowngradeDate: sub.scheduledDowngrade?.effectiveDate,
    };
  }, [data]);

  return {
    subscription,
    hasSubscription: !!subscription,
    isLoading: !error && !data && !!companyId,
    isValidating,
    isError: !!error && error.response?.status !== 404,
    refresh: mutate,
  };
};

/**
 * Hook pour récupérer TOUS les abonnements d'une entreprise
 * @param {string} companyId - ID de l'entreprise
 * @return {Object} Liste des abonnements
 */
export const useAllSubscriptions = (companyId) => {
  const endpointUrl = companyId ? endpoints.app.subscription.all(companyId) : null;

  const {data, error, mutate, isValidating} = useSWRWithSync(
      endpointUrl,
      fetcher,
      {
        eventTypes: APP_EVENTS.SUBSCRIPTIONS.LIST_UPDATED,
        companyId,
        swrOptions: {
          onError: () => {},
        },
      },
  );

  const subscriptions = useMemo(() => {
    if (!data?.success || !data?.data) return [];

    // Utilise isActive du backend, ajoute uniquement les champs dérivés manquants
    return data.data.map((sub) => ({
      ...sub,
      isCanceling: sub.cancelAtPeriodEnd && sub.isActive,
      hasScheduledDowngrade: !!(sub.scheduledDowngrade && sub.scheduledDowngrade.newPlanId && sub.scheduledDowngrade.effectiveDate),
      scheduledDowngradeDate: sub.scheduledDowngrade?.effectiveDate,
    }));
  }, [data]);

  return {
    subscriptions,
    hasSubscriptions: subscriptions.length > 0,
    isLoading: !error && !data && !!companyId,
    isValidating,
    isError: !!error,
    refresh: mutate,
  };
};

/**
 * Hook pour créer un nouvel abonnement
 * @return {Object} Fonction de création et état de chargement
 */
export const useCreateSubscription = () => {
  const [isLoading, setIsLoading] = useState(false);

  const createSubscription = async (companyId, subscriptionData) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
          endpoints.app.subscription.create(companyId),
          subscriptionData,
      );

      notifySubscriptionsUpdated(companyId, {action: 'create'});

      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la création de l\'abonnement',
        error: error.response?.data || error.message,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createSubscription,
    isLoading,
  };
};

/**
 * Hook pour annuler un abonnement
 * @return {Object} Fonction d'annulation et état de chargement
 */
export const useCancelSubscription = () => {
  const [isLoading, setIsLoading] = useState(false);

  const cancelSubscription = async (companyId, subscriptionId, cancelData = {}) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.patch(
          endpoints.app.subscription.cancel(companyId, subscriptionId),
          {
            reason: cancelData.reason || 'customer_request',
            immediately: cancelData.immediately || false,
          },
      );

      notifySubscriptionsUpdated(companyId, {action: 'cancel'});

      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de l\'annulation de l\'abonnement',
        error: error.response?.data || error.message,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    cancelSubscription,
    isLoading,
  };
};

/**
 * Hook pour réactiver un abonnement (annuler l'annulation)
 * @return {Object} Fonction de réactivation et état de chargement
 */
export const useReactivateSubscription = () => {
  const [isLoading, setIsLoading] = useState(false);

  const reactivateSubscription = async (companyId, subscriptionId) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
          endpoints.app.subscription.reactivate(companyId, subscriptionId),
      );

      notifySubscriptionsUpdated(companyId, {action: 'reactivate'});

      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la réactivation de l\'abonnement',
        error: error.response?.data || error.message,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    reactivateSubscription,
    isLoading,
  };
};

/**
 * Hook pour effectuer un upgrade d'abonnement
 * @return {Object} Fonction d'upgrade et état de chargement
 */
export const useUpgradeSubscription = () => {
  const [isLoading, setIsLoading] = useState(false);

  const upgradeSubscription = async (companyId, subscriptionId, newPlanId) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
          endpoints.app.subscription.upgrade(companyId, subscriptionId),
          {newPlanId},
      );

      notifySubscriptionsUpdated(companyId, {action: 'upgrade'});

      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de l\'upgrade de l\'abonnement',
        error: error.response?.data || error.message,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    upgradeSubscription,
    isLoading,
  };
};

/**
 * Hook pour effectuer un downgrade d'abonnement
 * @return {Object} Fonction de downgrade et état de chargement
 */
export const useDowngradeSubscription = () => {
  const [isLoading, setIsLoading] = useState(false);

  const downgradeSubscription = async (companyId, subscriptionId, newPlanId) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
          endpoints.app.subscription.downgrade(companyId, subscriptionId),
          {newPlanId},
      );

      notifySubscriptionsUpdated(companyId, {action: 'downgrade'});

      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors du downgrade de l\'abonnement',
        error: error.response?.data || error.message,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    downgradeSubscription,
    isLoading,
  };
};

/**
 * Hook pour annuler un downgrade programmé
 * @return {Object} Fonction d'annulation et état de chargement
 */
export const useCancelScheduledDowngrade = () => {
  const [isLoading, setIsLoading] = useState(false);

  const cancelScheduledDowngrade = async (companyId, subscriptionId) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.delete(
          endpoints.app.subscription.cancelScheduledDowngrade(companyId, subscriptionId),
      );

      notifySubscriptionsUpdated(companyId, {action: 'cancel_downgrade'});

      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de l\'annulation du downgrade',
        error: error.response?.data || error.message,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    cancelScheduledDowngrade,
    isLoading,
  };
};

/**
 * Hook pour synchroniser un abonnement avec Stripe
 * @return {Object} Fonction de synchronisation et état de chargement
 */
export const useSyncSubscription = () => {
  const [isLoading, setIsLoading] = useState(false);

  const syncSubscription = async (companyId, subscriptionId) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
          endpoints.app.subscription.sync(companyId, subscriptionId),
      );

      notifySubscriptionsUpdated(companyId, {action: 'sync'});

      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la synchronisation de l\'abonnement',
        error: error.response?.data || error.message,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    syncSubscription,
    isLoading,
  };
};

/**
 * Hook pour relancer le paiement de tous les abonnements en impayé
 *
 * Permet de réessayer en une seule action tous les paiements échoués.
 *
 * @return {Object} Fonction de relance globale et état de chargement
 */
export const useRetryAllPayments = () => {
  const [isLoading, setIsLoading] = useState(false);

  const retryAllPayments = async (companyId) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
          endpoints.app.subscription.retryAllPayments(companyId),
      );

      notifySubscriptionsUpdated(companyId, {action: 'retry_all_payments'});

      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la relance des paiements',
        error: error.response?.data || error.message,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    retryAllPayments,
    isLoading,
  };
};

/**
 * Hook combiné pour la gestion complète des abonnements d'une entreprise
 * Architecture refactorisée pour supporter plusieurs abonnements
 * @param {string} companyId - ID de l'entreprise
 * @return {Object} Toutes les données et fonctions liées aux abonnements
 */
export const useSubscriptionManager = (companyId) => {
  const {
    subscriptions,
    hasSubscriptions,
    isLoading: subscriptionsLoading,
    isValidating: subscriptionsValidating,
    refresh: refreshSubscriptions,
  } = useAllSubscriptions(companyId);

  const {createSubscription, isLoading: isCreating} = useCreateSubscription();
  const {cancelSubscription, isLoading: isCanceling} = useCancelSubscription();
  const {reactivateSubscription, isLoading: isReactivating} = useReactivateSubscription();
  const {upgradeSubscription, isLoading: isUpgrading} = useUpgradeSubscription();
  const {downgradeSubscription, isLoading: isDowngrading} = useDowngradeSubscription();
  const {cancelScheduledDowngrade, isLoading: isCancelingDowngrade} = useCancelScheduledDowngrade();
  const {syncSubscription, isLoading: isSyncing} = useSyncSubscription();

  const refreshAll = () => {
    refreshSubscriptions();
  };

  const isLoading = subscriptionsLoading;

  const getSubscriptionByModule = (module) => {
    return subscriptions.find((sub) => {
      const moduleKey = sub.moduleId?.moduleKey || sub.planId?.moduleId?.moduleKey;
      return moduleKey === module && (sub.isActive || sub.isCanceling);
    });
  };

  const hasActiveSubscription = (module) => {
    return !!getSubscriptionByModule(module);
  };

  const getActiveModules = () => {
    return subscriptions.filter((sub) => sub.isActive).map((sub) => sub.module);
  };

  return {
    subscriptions,
    hasSubscriptions,

    isLoading,
    isValidating: subscriptionsValidating,
    isCreating,
    isCanceling,
    isReactivating,
    isUpgrading,
    isDowngrading,
    isCancelingDowngrade,
    isSyncing,

    createSubscription,
    cancelSubscription,
    reactivateSubscription,
    upgradeSubscription,
    downgradeSubscription,
    cancelScheduledDowngrade,
    syncSubscription,
    refreshAll,
    refreshSubscriptions,

    getSubscriptionByModule,
    hasActiveSubscription,
    getActiveModules,
  };
};
